import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Stack, Typography, Drawer, ButtonBase, TextField, InputAdornment,
  Collapse, List, ListItemButton, ListItemText, useMediaQuery, useTheme
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useMyAccess, useAssetCategorySummary } from "@/hooks/useAssets";
import athrivIcon from "@/assets/athriv-icon.png";

export const RAIL_WIDTH = 76;
export const PANEL_WIDTH = 240;

const navItems = [
  { label: "Home", to: "/", icon: <DashboardOutlinedIcon /> },
  { label: "Assets", to: "/assets", icon: <Inventory2OutlinedIcon /> },
  { label: "Requests", to: "/asset-requests", icon: <AssignmentTurnedInOutlinedIcon /> },
  { label: "Assignments", to: "/assignments", icon: <SwapHorizOutlinedIcon /> },
  { label: "Maintenance", to: "/maintenance", icon: <BuildOutlinedIcon /> },
  { label: "Scan", to: "/scan", icon: <QrCodeScannerOutlinedIcon /> },
  { label: "Reports", to: "/reports", icon: <AssessmentOutlinedIcon /> }
];

/** Total width the fixed sidebar currently occupies - the Assets panel only
 * shows while on an Assets route, so TopHeader/AppLayout need this to offset
 * correctly rather than assuming a single fixed width. On mobile the sidebar
 * is an overlay drawer that doesn't take up layout space, so the offset is 0. */
export function useSidebarWidth(): number {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  if (isMobile) return 0;
  const isAssetsSection = pathname.startsWith("/assets");
  return isAssetsSection ? RAIL_WIDTH + PANEL_WIDTH : RAIL_WIDTH;
}

function AssetsPanel({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(true);
  const { data: access } = useMyAccess();
  const { data: categories } = useAssetCategorySummary(access?.isElevated ?? false);

  const filteredCategories = useMemo(
    () => (categories ?? []).filter((c) => c.categoryName.toLowerCase().includes(search.toLowerCase())),
    [categories, search]
  );

  const currentCategoryId = new URLSearchParams(location.search).get("categoryId");
  const isAllAssetsActive = location.pathname === "/assets" && !currentCategoryId;

  const goTo = (to: string) => {
    navigate(to);
    onNavigate?.();
  };

  return (
    <Box sx={{ height: "100%", overflowY: "auto", py: 2, px: 1.5 }}>
      <TextField
        size="small"
        fullWidth
        placeholder="Search categories"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      <ListItemButton
        selected={isAllAssetsActive}
        onClick={() => goTo("/assets")}
        sx={{ borderRadius: 1.5, mb: 0.5 }}
      >
        <ListItemText primary="All Assets" primaryTypographyProps={{ fontWeight: 700, fontSize: "0.9rem" }} />
        <Box onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }} sx={{ display: "flex" }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </Box>
      </ListItemButton>

      {access?.isElevated && (
        <Collapse in={expanded}>
          <List dense disablePadding sx={{ pl: 1.5 }}>
            {filteredCategories.map((c) => (
              <ListItemButton
                key={c.categoryId}
                selected={currentCategoryId === c.categoryId}
                onClick={() => goTo(`/assets?categoryId=${c.categoryId}`)}
                sx={{ borderRadius: 1.5, py: 0.5 }}
              >
                <ListItemText
                  primary={`${c.categoryName} (${c.total})`}
                  primaryTypographyProps={{ fontSize: "0.82rem" }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}

      {access?.isElevated && (
        <ListItemButton onClick={() => goTo("/assets/import")} sx={{ borderRadius: 1.5, mt: 1.5 }}>
          <UploadFileOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
          <ListItemText primary="Import Assets" primaryTypographyProps={{ fontSize: "0.85rem" }} />
        </ListItemButton>
      )}

      {access?.isElevated && (
        <ListItemButton onClick={() => goTo("/assets/generate-codes")} sx={{ borderRadius: 1.5 }}>
          <QrCodeScannerOutlinedIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
          <ListItemText primary="Generate Barcodes" primaryTypographyProps={{ fontSize: "0.85rem" }} />
        </ListItemButton>
      )}
    </Box>
  );
}

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ mobileOpen = false, onClose }: SidebarProps) {
  const { pathname } = useLocation();
  const { data: access } = useMyAccess();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const showTeamNav = access?.isElevated && access?.isEmailAuthTenant;
  const showUsersAdminNav = access?.isElevated;
  const isAssetsSection = pathname.startsWith("/assets");
  const totalWidth = isAssetsSection ? RAIL_WIDTH + PANEL_WIDTH : RAIL_WIDTH;
  const handleNavClick = isMobile ? onClose : undefined;

  const content = (
    <>
      {/* Icon rail */}
      <Box sx={{ width: RAIL_WIDTH, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: isAssetsSection ? "1px solid" : "none", borderColor: "divider" }}>
        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <Box component="img" src={athrivIcon} alt="Athriv Technologies" sx={{ width: 34, height: 34, objectFit: "contain" }} />
        </Box>

        <Stack spacing={0.5} sx={{ px: 1 }}>
          {navItems.map((item) => (
            <ButtonBase
              key={item.to}
              component={NavLink}
              to={item.to}
              end={item.to === "/"}
              onClick={handleNavClick}
              sx={{
                borderRadius: 2, py: 1, display: "flex", flexDirection: "column", gap: 0.5,
                color: "text.secondary",
                "&.active": { color: "primary.main", bgcolor: "action.selected" }
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>{item.label}</Typography>
            </ButtonBase>
          ))}

          {showTeamNav && (
            <ButtonBase
              component={NavLink}
              to="/team"
              onClick={handleNavClick}
              sx={{
                borderRadius: 2, py: 1, display: "flex", flexDirection: "column", gap: 0.5,
                color: "text.secondary",
                "&.active": { color: "primary.main", bgcolor: "action.selected" }
              }}
            >
              <GroupOutlinedIcon />
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>Team</Typography>
            </ButtonBase>
          )}

          {showUsersAdminNav && (
            <ButtonBase
              component={NavLink}
              to="/admin/users"
              onClick={handleNavClick}
              sx={{
                borderRadius: 2, py: 1, display: "flex", flexDirection: "column", gap: 0.5,
                color: "text.secondary",
                "&.active": { color: "primary.main", bgcolor: "action.selected" }
              }}
            >
              <AdminPanelSettingsOutlinedIcon />
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>Users</Typography>
            </ButtonBase>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ px: 1, pb: 2 }}>
          <ButtonBase
            component={NavLink}
            to="/settings"
            onClick={handleNavClick}
            sx={{
              borderRadius: 2, py: 1, width: "100%", display: "flex", flexDirection: "column", gap: 0.5,
              color: "text.secondary",
              "&.active": { color: "primary.main", bgcolor: "action.selected" }
            }}
          >
            <SettingsOutlinedIcon />
            <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>Settings</Typography>
          </ButtonBase>
        </Box>
      </Box>

      {/* Expandable Assets panel */}
      {isAssetsSection && (
        <Box sx={{ width: PANEL_WIDTH, flexShrink: 0 }}>
          <AssetsPanel onNavigate={handleNavClick} />
        </Box>
      )}
    </>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? undefined : totalWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: totalWidth,
          maxWidth: "85vw",
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "row"
        }
      }}
    >
      {content}
    </Drawer>
  );
}
